'use client';

import type { EventHandler, FC, MouseEvent, ReactNode } from 'react';
import { cx } from '../../utils/cx';
import { Reveal } from '../Reveal';
import styles from './Button.module.css';

export interface ButtonProps {
  children?: ReactNode;
  variant?: 'solid' | 'hollow';
  size?: 'S' | 'M';
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  disabled?: boolean;
  frameAnimation?: 'none' | 'pulsing';
  onClick?: EventHandler<MouseEvent<HTMLButtonElement>>;
}

export const Button: FC<ButtonProps> = ({
  children,
  variant = 'solid',
  size = 'M',
  leadingIcon,
  trailingIcon,
  disabled,
  frameAnimation = 'none',
  onClick,
}) => {
  const divider =
    size === 'S' ? null : (
      <span className={styles.divider} data-variant={variant} />
    );

  const revealedDivider =
    variant === 'hollow' ? (
      <Reveal size={120} spread={0.5}>
        {divider}
      </Reveal>
    ) : (
      divider
    );

  return (
    <div
      className={styles.root}
      data-size={size}
      data-frame-animation={frameAnimation}
    >
      <Reveal inactiveOpacity={0.7} size={240} spread={0.5}>
        <button
          className={styles.button}
          data-variant={variant}
          data-size={size}
          disabled={disabled}
          onClick={onClick}
        >
          <Reveal
            className={cx(
              styles.border,
              size === 'S' && styles.borderSmall,
              variant === 'hollow' && styles.borderHollow
            )}
            size={120}
            spread={0.5}
          />

          {leadingIcon && (
            <>
              <span className={cx(styles.icon, styles.leadingIcon)}>
                {leadingIcon}
              </span>
              {revealedDivider}
            </>
          )}

          <span className={styles.label}>{children}</span>

          {trailingIcon && (
            <>
              {revealedDivider}
              <span className={cx(styles.icon, styles.trailingIcon)}>
                {trailingIcon}
              </span>
            </>
          )}
        </button>
      </Reveal>
    </div>
  );
};
